import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FaDownload, FaTable, FaSpinner, FaTriangleExclamation, FaChartColumn, FaArrowsRotate } from 'react-icons/fa6';
import { useEvaluationTables } from './hooks';
import { DataTable } from './DataTable';
import { convertToTableData } from './utils';
import { EvaluationTablesProps } from './types';

/**
 * EvaluationTables Component
 *
 * Displays df_eval and df_eval_ts tables after model training
 * Shows model performance metrics and configuration details
 */
export const EvaluationTables: React.FC<EvaluationTablesProps> = ({ sessionId, sessionName }) => {
  const { t } = useTranslation('training');

  const {
    dfEval,
    dfEvalTs,
    loading,
    error,
    saveMessage,
    fetchEvaluationTables,
    downloadEvaluationTables,
    downloadEvaluationTablesTs
  } = useEvaluationTables(sessionId);

  // Convert raw data to TableData format
  const tables = useMemo(() => {
    if (!dfEval) return [];
    return convertToTableData(dfEval, (featureName) =>
      t('training.evaluation.performanceMetrics', { featureName })
    );
  }, [dfEval, t]);

  if (loading) {
    return (
      <div className="rounded-xl border border-border bg-card overflow-hidden shadow-sm">
        <div className="flex flex-col items-center justify-center py-16">
          <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-accent mb-3">
            <FaSpinner className="animate-spin text-primary text-sm" />
          </div>
          <p className="text-sm text-muted-foreground">{t('training.evaluation.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-red-200/60 bg-card overflow-hidden shadow-sm">
        <div className="flex flex-col items-center justify-center py-14 px-6">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gradient-to-br from-red-500 to-red-700 mb-4">
            <FaTriangleExclamation className="text-white text-base" />
          </div>
          <h3 className="text-sm sm:text-base font-semibold text-foreground mb-1.5 tracking-tight">
            {t('training.evaluation.errorTitle')}
          </h3>
          <p className="text-xs sm:text-sm text-muted-foreground mb-5 text-center max-w-sm leading-relaxed">{error}</p>
          <button
            type="button"
            onClick={fetchEvaluationTables}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-xs sm:text-sm font-medium text-primary-foreground bg-primary rounded-lg hover:bg-primary/90 active:scale-[0.98] transition-all duration-150"
          >
            <FaArrowsRotate className="text-[10px]" />
            {t('training.evaluation.retry')}
          </button>
        </div>
      </div>
    );
  }

  if (!dfEval || !dfEvalTs) {
    return (
      <div className="rounded-xl border border-border bg-card overflow-hidden shadow-sm">
        <div className="flex flex-col items-center justify-center py-14 px-6">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-muted mb-4">
            <FaChartColumn className="text-muted-foreground text-base" />
          </div>
          <p className="text-sm text-muted-foreground text-center">
            {t('training.evaluation.noTables', { sessionId })}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base sm:text-lg font-semibold text-foreground tracking-tight">
            {t('training.evaluation.title')}
          </h2>
          <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
            {t('training.evaluation.subtitle', { sessionId: sessionName || sessionId })}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {saveMessage && (
            <span className={`text-xs font-medium ${saveMessage.includes('success') || saveMessage.includes('Success') ? 'text-emerald-600' : 'text-red-500'}`}>
              {saveMessage}
            </span>
          )}
          <button
            type="button"
            onClick={downloadEvaluationTables}
            disabled={!dfEval || !dfEvalTs}
            title={t('training.evaluation.downloadTables')}
            className="w-8 h-8 inline-flex items-center justify-center rounded-lg text-muted-foreground bg-card border border-border hover:bg-muted hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.98] transition-all duration-150 shadow-sm"
          >
            <FaTable className="text-xs" />
          </button>
          <button
            type="button"
            onClick={downloadEvaluationTablesTs}
            disabled={!dfEvalTs}
            title={t('training.evaluation.downloadTablesTs')}
            className="w-8 h-8 inline-flex items-center justify-center rounded-lg text-muted-foreground bg-card border border-border hover:bg-muted hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.98] transition-all duration-150 shadow-sm"
          >
            <FaDownload className="text-xs" />
          </button>
        </div>
      </div>

      {/* df_eval tables - Model Performance Metrics for each output feature */}
      <div className="space-y-4">
        {tables.map((table, index) => (
          <DataTable key={index} tableData={table} />
        ))}
      </div>
    </div>
  );
};

export default EvaluationTables;
